<script setup lang="ts">
/**
 * UiDevCard — placeholder для секций где backend ещё не реализован
 * (inbox-121).
 *
 * Dashed-border 1px var(--accent) + bg rgba(79,140,255,0.04) +
 * construction-cone SVG icon 24px var(--warning) слева.
 *
 * Props:
 *   feature — заголовок секции (e.g. «Биллинг»)
 *   message — текст под заголовком (default «Скоро. Сейчас в разработке.»)
 *
 * Default slot — дополнительный контент (ссылка на трекер, ETA и т.п.),
 * рендерится мелким шрифтом ниже message.
 *
 * Usage:
 *   <UiDevCard feature="Биллинг" />
 *   <UiDevCard
 *     feature="2FA"
 *     message="Поддержка TOTP появится в апреле."
 *   >
 *     <a href="https://tracker/short-link/inbox-105">tracker #105</a>
 *   </UiDevCard>
 */

interface Props {
  feature: string;
  message?: string;
  /** inbox-481 — compact inline-вариант для модалок и узких контейнеров:
   *  solid border вместо dashed, плотный padding, мелкий icon.
   *  Default false — сохраняет section-card вид для standalone usage. */
  compact?: boolean;
}

withDefaults(defineProps<Props>(), {
  message: 'Скоро. Сейчас в разработке.',
  compact: false,
});
</script>

<template>
  <div :class="['dev-card', { compact }]" role="status">
    <svg
      class="dev-icon"
      viewBox="0 0 24 24"
      :width="compact ? 16 : 24"
      :height="compact ? 16 : 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2 L4 22 L20 22 Z" />
      <path d="M7 13 L17 13" />
      <path d="M8.5 8 L15.5 8" />
    </svg>
    <div class="dev-body">
      <div class="dev-title">{{ feature }}</div>
      <div class="dev-msg">{{ message }}</div>
      <div v-if="$slots.default" class="dev-extra"><slot /></div>
    </div>
  </div>
</template>

<style scoped>
.dev-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  border: 1px dashed var(--accent);
  background: rgba(79, 140, 255, 0.04);
  padding: 24px;
  border-radius: var(--radius-card, 12px);
}
/* inbox-481 — compact-вариант для модалок: solid border + bg-elev,
   плотный padding (10/12), мелкий icon, fz титула/msg уменьшены.
   Выглядит как inline-подсказка, не section-card. */
.dev-card.compact {
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  background: var(--bg-elev);
  border-radius: 8px;
}
.dev-card.compact .dev-title { font-size: 12.5px; margin-bottom: 2px; }
.dev-card.compact .dev-msg { font-size: 12px; line-height: 1.4; }
.dev-card.compact .dev-extra { margin-top: 6px; font-size: 11.5px; }

.dev-icon {
  color: var(--warning);
  flex-shrink: 0;
  margin-top: 2px;
}

.dev-body {
  flex: 1;
  min-width: 0;
}

.dev-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--fg);
  margin-bottom: 4px;
}

.dev-msg {
  font-size: 13px;
  color: var(--fg-muted);
  line-height: 1.5;
}

.dev-extra {
  margin-top: 10px;
  font-size: 12px;
  color: var(--fg-dim);
}

.dev-extra :slotted(a) {
  color: var(--accent-hi);
  text-decoration: underline;
}
</style>
